import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MessageSquare, Download, UserPlus, Link2, Compass, Apple, Smartphone, Monitor, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const DL = [
  { key: "ios", icon: Apple, href: "https://apps.apple.com/app/discord-chat-talk-hangout/id985746746" },
  { key: "android", icon: Smartphone, href: "https://play.google.com/store/apps/details?id=com.discord" },
  { key: "desktop", icon: Monitor, href: "https://discord.com/download" },
];
const STEPS = [MessageSquare, Download, UserPlus, Link2, Compass];

function LinkButton() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const go = async () => {
    if (!user) return navigate("/login");
    if (user.discord_linked) return navigate("/account?tab=discord");
    try {
      const { data } = await api.get("/discord/oauth/url");
      if (data?.url) window.location.href = data.url;
      else navigate("/account?tab=discord");
    } catch { navigate("/account?tab=discord"); }
  };
  const label = user === null ? "…" : !user ? t("discordGuide.signInToLink") : user.discord_linked ? t("discordGuide.alreadyLinked") : t("discordGuide.linkNow");
  return (
    <Button onClick={go} disabled={user === null} data-testid="discord-guide-link-btn" className="mt-4 bg-[#5865F2] hover:bg-[#4752C4] text-white h-11 px-5 gap-2">
      <Link2 size={15} /> {label}
    </Button>
  );
}

function StepExtra({ i }) {
  const { t } = useTranslation();
  if (i === 1) return (
    <div className="mt-4 flex flex-wrap gap-2">
      {DL.map(({ key, icon: Icon, href }) => (
        <a key={key} href={href} target="_blank" rel="noreferrer" data-testid={`discord-guide-download-${key}`}
          className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 h-10 text-sm font-semibold text-slate-900 hover:border-[#5865F2] hover:text-[#5865F2] transition-colors">
          <Icon size={15} /> {t(`discordGuide.dl.${key}`)}
        </a>
      ))}
    </div>
  );
  if (i === 2) return (
    <a href="https://discord.com/register" target="_blank" rel="noreferrer" data-testid="discord-guide-register-link"
      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#5865F2] hover:underline">
      discord.com/register <ArrowRight size={14} />
    </a>
  );
  if (i === 3) return <LinkButton />;
  return null;
}

export default function DiscordGuide() {
  const { t } = useTranslation();
  return (
    <div data-testid="discord-guide-page" className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <div className="text-xs uppercase tracking-[0.28em] text-[#5865F2] font-bold">— {t("discordGuide.eyebrow")}</div>
        <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl tracking-tight text-slate-900">{t("discordGuide.title")}</h1>
        <p className="mt-4 text-base text-slate-600 max-w-2xl leading-relaxed">{t("discordGuide.intro")}</p>
        <ol className="mt-12 space-y-5">
          {STEPS.map((Icon, i) => (
            <li key={i} data-testid={`discord-guide-step-${i + 1}`}
              className="relative bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 grid grid-cols-[auto_1fr] gap-5 opacity-0 animate-[fadeUp_.5s_ease-out_forwards]"
              style={{ animationDelay: `${i * 90}ms` }}>
              <div className="w-12 h-12 rounded-xl bg-[#5865F2]/10 text-[#5865F2] flex items-center justify-center"><Icon size={22} /></div>
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">{t("discordGuide.step", { n: i + 1 })}</div>
                <h2 className="mt-1 font-display font-bold text-lg text-slate-900">{t(`discordGuide.s${i + 1}.title`)}</h2>
                <p className="mt-2 text-sm sm:text-base text-slate-600 leading-relaxed">{t(`discordGuide.s${i + 1}.body`)}</p>
                <StepExtra i={i} />
              </div>
            </li>
          ))}
        </ol>
        <p className="mt-10 text-sm text-slate-500">{t("discordGuide.help")}</p>
      </div>
      <style>{"@keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}"}</style>
    </div>
  );
}
